import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router';

import { Modal } from 'components/ui';

import { useDispatch, useSelector } from 'hooks';
import { login } from 'modules/auth/thunk';
import { setLoginData } from 'modules/auth/actions';
import type { PhoneNumber } from 'modules/selfServices/types/SelfService';
import {
  getLoginData,
  hasLocationIdError as hasLocationIdErrorSelector
} from 'modules/auth/selectors';
import { isLoadingLogin } from 'modules/selfServices/selectors';

import KioskModalActions from './KioskModalActions';
import useEnterSubmit from '../../hooks/useEnterSubmit';
import PhoneNumberInput from 'components/ui/PhoneNumberInput';
import { getConnectionTypes } from 'modules/dealers/selectors';
import { SelfOfficeLoginMethod } from 'modules/dealers/types/ContextResponse';
import { LoginModals as PhoneNumberModalProps } from './types';
import KioskLocationIdInput from 'components/Login/KioskLocationIdInput';



const PhoneNumberModal: React.FC<PhoneNumberModalProps> = ({ onClose, onError, onSuccess }) => {
  const dispatch = useDispatch();
  const { state: pageState } = useLocation();
  const isAtDealership = pageState?.atDealership;

  const loginData = useSelector(getLoginData);
  const isLoading = useSelector(isLoadingLogin);
  const connectionTypes = useSelector(getConnectionTypes);
  const hasLocationIdError = useSelector(hasLocationIdErrorSelector);

  const { phoneNumber = '', locationId } = loginData;
  const [hasPhoneError, setHasPhoneError] = React.useState(true);

  const handleOnChange = (value: Partial<PhoneNumber>) =>{
    dispatch(setLoginData({ ...loginData, phoneNumber: value }));
  };

  const locationIdValidation = !isAtDealership || Boolean(locationId);
  const canSubmit = !hasPhoneError && Boolean(phoneNumber?.local?.length) && locationIdValidation;

  const handleSubmit = async () => {
    try {
      await dispatch(login({
        phoneNumber,
        connectionTypes,
        type: pageState?.type,
        searchType: SelfOfficeLoginMethod.PHONE_NUMBER,
        locationId: isAtDealership ? locationId : undefined
      })).unwrap();
      onSuccess(SelfOfficeLoginMethod.PHONE_NUMBER);
    } catch (error) {
      onError(error);
    }
  };

  const handleLocationIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    dispatch(setLoginData({ ...loginData, locationId: value ? Number(value) : undefined }));
  };

  const handleKeyDown = useEnterSubmit(handleSubmit, canSubmit);

  return (
    <Modal
      open
      onClose={onClose}
      className="max-w-modal min-w-[50%]!"
      title={
        <div role="heading" aria-level={2} data-testid="phone-number-modal-title" className="text-icon font-bold">
          <FormattedMessage id="login.phoneNumberModal.title" defaultMessage="Phone number" />
        </div>
      }
    >
      <div className="flex flex-col gap-9">
        <div className="text-center text-s">
          <FormattedMessage
            id="login.phoneNumberModal.label"
            defaultMessage="Access your appointment with your phone number. A verification code will be sent to your phone by SMS."
            tagName="span"
          />
        </div>
        <PhoneNumberInput
          required
          value={phoneNumber}
          onKeyDown={handleKeyDown}
          onChange={handleOnChange}
          onError={setHasPhoneError}
        />
        {isAtDealership && (
          <KioskLocationIdInput
            value={locationId}
            canSubmit={canSubmit}
            onSubmit={handleSubmit}
            error={hasLocationIdError}
            onChange={handleLocationIdChange}
          />
        )}
        <KioskModalActions
          onClose={onClose}
          onSubmit={handleSubmit}
          isLoading={isLoading}
          disabled={!canSubmit}
        />
      </div>
    </Modal>
  );
};

export default PhoneNumberModal;
